/**
 * useRangeTranslations hook
 *
 * V2 Live Testing에서 range 질문의 스케일 라벨 번역을 로드하고
 * PaDataRepoState.translations에 설정하는 hook
 */

import { useEffect, useMemo, useRef } from 'react';
import { useSetRecoilState } from 'recoil';
import { translationControllerSearch } from '../../../../api/generated/translation/translation';
import type { SearchTranslationResponseDto } from '../../../../api/generated/types';
import { PaDataRepoState } from '../../../../../report/use-data';
import { genScaleLabelKey } from '../questions/useQuestionConfigureModal';

interface RangeQuestionInfo {
  questionKey: string;
  scaleLabels: number[];
}

interface UseRangeTranslationsProps {
  surveyId?: string | number;
  /** range 타입 질문들의 정보 (questionKey + scaleLabels 값 배열) */
  rangeQuestions: RangeQuestionInfo[];
  locale: string;
  enabled?: boolean;
}

export function useRangeTranslations({
  surveyId,
  rangeQuestions,
  locale,
  enabled = true,
}: UseRangeTranslationsProps) {
  const setDataState = useSetRecoilState(PaDataRepoState);
  const loadedRef = useRef<string | null>(null);

  // 배열 참조 안정화 - JSON 기반으로 memoize
  const stableStr = useMemo(
    () => JSON.stringify(rangeQuestions.map((q) => ({ k: q.questionKey, s: q.scaleLabels }))),
    [rangeQuestions],
  );
  const stableQuestions = useMemo(() => {
    try { return JSON.parse(stableStr) as Array<{ k: string; s: number[] }>; }
    catch { return []; }
  }, [stableStr]);

  useEffect(() => {
    if (!enabled || !surveyId || !stableQuestions.length || !locale) {
      return;
    }

    const cacheKey = `range-${surveyId}-${stableStr}-${locale}`;
    if (loadedRef.current === cacheKey) {
      return;
    }
    // 즉시 캐시 설정하여 중복 호출 방지
    loadedRef.current = cacheKey;

    // Scale label 번역 키 목록 생성
    const keys: string[] = [];
    stableQuestions.forEach(({ k: qKey, s: scaleLabels }) => {
      (scaleLabels || []).forEach((val) => {
        keys.push(genScaleLabelKey(qKey, val));
      });
    });

    if (keys.length === 0) return;

    async function loadTranslations() {
      try {
        const response = (await translationControllerSearch({
          surveyId: Number(surveyId),
          keys,
          locale,
          type: 'patient',
        })) as SearchTranslationResponseDto;

        const apiTranslations = response?.translations || [];

        if (apiTranslations.length > 0) {
          const formattedTranslations = apiTranslations.map((t) => ({
            id: t.id?.toString() || null,
            key: t.key || '',
            value: t.value || null,
            locale: t.locale || null,
            type: null,
            dataType: null,
          }));

          setDataState((prev) => {
            const existingTranslations = prev.translations || [];
            const newTranslationsMap = new Map<string, (typeof prev.translations)[number]>();

            existingTranslations.forEach((tr) => {
              const uniqueKey = `${tr.key}-${tr.locale}`;
              newTranslationsMap.set(uniqueKey, tr);
            });

            formattedTranslations.forEach((tr) => {
              const uniqueKey = `${tr.key}-${tr.locale}`;
              newTranslationsMap.set(uniqueKey, tr as (typeof prev.translations)[number]);
            });

            return {
              ...prev,
              translations: Array.from(newTranslationsMap.values()),
            };
          });
        }
      } catch (error) {
        console.error('Failed to load Range translations:', error);
        // 실패 시 캐시 리셋하여 재시도 가능하게
        loadedRef.current = null;
      }
    }

    loadTranslations();
  }, [surveyId, stableQuestions, stableStr, locale, enabled, setDataState]);
}
